import React from 'react';
import { useTranslation } from '../i18n/LanguageContext';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

const AGB = () => {
  const { t, language } = useTranslation();

  return (
    <div className="min-h-screen bg-white">
      {/* Simple Header */}
      <header className="bg-brand-dark text-white py-6">
        <div className="container-cift">
          <Link to="/" className="inline-flex items-center gap-2 text-white hover:text-cream transition-colors">
            <ArrowLeft size={20} />
            {language === 'de' ? 'Zurück zur Startseite' : 'Back to Home'}
          </Link>
        </div>
      </header>

      {/* Content */}
      <div className="container-narrow py-16">
        <h1 className="section-title mb-12">
          {language === 'de' ? 'Allgemeine Geschäftsbedingungen' : 'Terms and Conditions'}
        </h1>

        <div className="prose max-w-none space-y-8">
          {/* Geltungsbereich */}
          <div>
            <h2 className="text-2xl font-display font-bold mb-4">
              {language === 'de' ? '1. Geltungsbereich' : '1. Scope of Application'}
            </h2>
            <p className="text-gray-600 leading-relaxed">
              {language === 'de'
                ? 'Diese Allgemeinen Geschäftsbedingungen (AGB) gelten für alle Verträge zwischen CIFT Studio (im Folgenden "Auftragnehmer") und dem Kunden (im Folgenden "Auftraggeber") über die Erbringung von Foto- und Videografiedienstleistungen.'
                : 'These General Terms and Conditions (GTC) apply to all contracts between CIFT Studio (hereinafter "Contractor") and the customer (hereinafter "Client") for the provision of photography and videography services.'}
            </p>
          </div>

          {/* Vertragsschluss */}
          <div>
            <h2 className="text-2xl font-display font-bold mb-4">
              {language === 'de' ? '2. Vertragsschluss' : '2. Conclusion of Contract'}
            </h2>
            <p className="text-gray-600 leading-relaxed mb-4">
              {language === 'de'
                ? 'Der Vertrag kommt durch die schriftliche Bestätigung des Auftrags durch den Auftragnehmer zustande. Die Leistungsbeschreibung ergibt sich aus dem individuellen Angebot.'
                : 'The contract is concluded by the written confirmation of the order by the contractor. The service description results from the individual offer.'}
            </p>
            <p className="text-gray-600 leading-relaxed">
              {language === 'de'
                ? 'Mit Vertragsschluss wird eine Anzahlung in Höhe von [X]% der Gesamtsumme fällig. Der Restbetrag ist spätestens 7 Tage vor dem vereinbarten Termin zu zahlen.'
                : 'Upon conclusion of the contract, a down payment of [X]% of the total amount is due. The remaining amount must be paid no later than 7 days before the agreed date.'}
            </p>
          </div>

          {/* Leistungsumfang */}
          <div>
            <h2 className="text-2xl font-display font-bold mb-4">
              {language === 'de' ? '3. Leistungsumfang' : '3. Scope of Services'}
            </h2>
            <p className="text-gray-600 leading-relaxed mb-4">
              {language === 'de'
                ? 'Der Leistungsumfang ergibt sich aus der individuellen Vereinbarung. Alle Leistungen, die über den vereinbarten Umfang hinausgehen, werden separat berechnet.'
                : 'The scope of services results from the individual agreement. All services that go beyond the agreed scope will be charged separately.'}
            </p>
            <p className="text-gray-600 leading-relaxed">
              {language === 'de'
                ? 'Der Auftragnehmer behält sich das Recht vor, die Leistung durch qualifizierte Dritte erbringen zu lassen.'
                : 'The contractor reserves the right to have the service performed by qualified third parties.'}
            </p>
          </div>

          {/* Lieferung */}
          <div>
            <h2 className="text-2xl font-display font-bold mb-4">
              {language === 'de' ? '4. Lieferung der Aufnahmen' : '4. Delivery of Recordings'}
            </h2>
            <p className="text-gray-600 leading-relaxed mb-4">
              {language === 'de'
                ? 'Die bearbeiteten Fotos werden digital bereitgestellt. Die Lieferfrist beträgt in der Regel:'
                : 'The edited photos are provided digitally. The delivery period is usually:'}
            </p>
            <ul className="list-disc list-inside text-gray-600 space-y-2 mb-4">
              <li>{language === 'de' ? 'Fotos: 3-4 Wochen nach dem Event' : 'Photos: 3-4 weeks after the event'}</li>
              <li>{language === 'de' ? 'Videos: 6-8 Wochen nach dem Event' : 'Videos: 6-8 weeks after the event'}</li>
            </ul>
            <p className="text-gray-600 leading-relaxed">
              {language === 'de'
                ? 'Die Lieferfristen sind unverbindlich, es sei denn, es wurde ausdrücklich eine verbindliche Frist vereinbart.'
                : 'Delivery periods are non-binding unless a binding deadline has been expressly agreed.'}
            </p>
          </div>

          {/* Urheberrecht */}
          <div>
            <h2 className="text-2xl font-display font-bold mb-4">
              {language === 'de' ? '5. Urheberrecht und Nutzungsrechte' : '5. Copyright and Usage Rights'}
            </h2>
            <p className="text-gray-600 leading-relaxed mb-4">
              {language === 'de'
                ? 'Der Auftragnehmer behält sich alle Rechte an den erstellten Aufnahmen vor. Mit vollständiger Bezahlung erhält der Auftraggeber ein einfaches, nicht ausschließliches Nutzungsrecht für den privaten bzw. geschäftlichen Gebrauch.'
                : 'The contractor reserves all rights to the recordings made. Upon full payment, the client receives a simple, non-exclusive right of use for private or commercial use.'}
            </p>
            <p className="text-gray-600 leading-relaxed mb-4">
              {language === 'de'
                ? 'Der Auftragnehmer ist berechtigt, die Aufnahmen zu Werbezwecken, für Veröffentlichungen in sozialen Medien und auf der eigenen Website zu verwenden.'
                : 'The contractor is entitled to use the recordings for advertising purposes, for publications on social media and on their own website.'}
            </p>
            <p className="text-gray-600 leading-relaxed">
              {language === 'de'
                ? 'Die Weitergabe der Aufnahmen an Dritte zu kommerziellen Zwecken bedarf der ausdrücklichen Zustimmung des Auftragnehmers.'
                : 'The transfer of recordings to third parties for commercial purposes requires the express consent of the contractor.'}
            </p>
          </div>

          {/* Stornierung */}
          <div>
            <h2 className="text-2xl font-display font-bold mb-4">
              {language === 'de' ? '6. Stornierung und Rücktritt' : '6. Cancellation and Withdrawal'}
            </h2>
            <p className="text-gray-600 leading-relaxed mb-4">
              {language === 'de' ? 'Stornierungsbedingungen:' : 'Cancellation conditions:'}
            </p>
            <ul className="list-disc list-inside text-gray-600 space-y-2 mb-4">
              <li>{language === 'de' ? 'Bis 90 Tage vor dem Termin: Kostenlose Stornierung' : 'Up to 90 days before the date: Free cancellation'}</li>
              <li>{language === 'de' ? 'Bis 60 Tage vor dem Termin: 50% der Gesamtsumme' : 'Up to 60 days before the date: 50% of the total amount'}</li>
              <li>{language === 'de' ? 'Bis 30 Tage vor dem Termin: 75% der Gesamtsumme' : 'Up to 30 days before the date: 75% of the total amount'}</li>
              <li>{language === 'de' ? 'Weniger als 30 Tage vor dem Termin: 100% der Gesamtsumme' : 'Less than 30 days before the date: 100% of the total amount'}</li>
            </ul>
            <p className="text-gray-600 leading-relaxed">
              {language === 'de'
                ? 'Bei höherer Gewalt (z.B. Krankheit, Unfall) wird eine individuelle Lösung gefunden.'
                : 'In case of force majeure (e.g. illness, accident), an individual solution will be found.'}
            </p>
          </div>

          {/* Haftung */}
          <div>
            <h2 className="text-2xl font-display font-bold mb-4">
              {language === 'de' ? '7. Haftung' : '7. Liability'}
            </h2>
            <p className="text-gray-600 leading-relaxed mb-4">
              {language === 'de'
                ? 'Der Auftragnehmer haftet für Vorsatz und grobe Fahrlässigkeit. Bei leichter Fahrlässigkeit haftet der Auftragnehmer nur bei Verletzung wesentlicher Vertragspflichten.'
                : 'The contractor is liable for intent and gross negligence. In case of slight negligence, the contractor is only liable for breach of essential contractual obligations.'}
            </p>
            <p className="text-gray-600 leading-relaxed">
              {language === 'de'
                ? 'Der Auftragnehmer haftet nicht für den Verlust von Daten durch technisches Versagen der Ausrüstung. Es wird empfohlen, eine Hochzeitsversicherung abzuschließen.'
                : 'The contractor is not liable for loss of data due to technical failure of equipment. It is recommended to take out wedding insurance.'}
            </p>
          </div>

          {/* Schlussbestimmungen */}
          <div>
            <h2 className="text-2xl font-display font-bold mb-4">
              {language === 'de' ? '8. Schlussbestimmungen' : '8. Final Provisions'}
            </h2>
            <p className="text-gray-600 leading-relaxed mb-4">
              {language === 'de'
                ? 'Es gilt das Recht der Bundesrepublik Deutschland. Erfüllungsort und Gerichtsstand ist [Ort], soweit gesetzlich zulässig.'
                : 'The law of the Federal Republic of Germany applies. Place of performance and jurisdiction is [Place], as far as legally permissible.'}
            </p>
            <p className="text-gray-600 leading-relaxed">
              {language === 'de'
                ? 'Sollten einzelne Bestimmungen dieser AGB unwirksam sein, berührt dies die Wirksamkeit der übrigen Bestimmungen nicht.'
                : 'Should individual provisions of these GTC be invalid, this does not affect the validity of the remaining provisions.'}
            </p>
          </div>

          {/* Hinweis */}
          <div className="bg-cream p-6 rounded-lg mt-12">
            <p className="text-sm text-gray-600 italic">
              {language === 'de'
                ? '⚠️ Hinweis: Diese AGB sind ein Muster und sollten von einem Rechtsanwalt geprüft und an Ihre spezifischen Geschäftsbedingungen angepasst werden. Insbesondere sollten die Platzhalter [...] und X durch konkrete Angaben ersetzt werden.'
                : '⚠️ Note: These GTC are a template and should be reviewed by a lawyer and adapted to your specific business conditions. In particular, the placeholders [...] and X should be replaced with specific information.'}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AGB;
